import CircleIcon from '@mui/icons-material/Circle';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

export type GroupHealth = 'ownerless' | 'managerless' | 'inactive' | 'healthy';

interface HealthConfig {
  label: string;
  lightColor: string;
  darkColor: string;
}

const HEALTH_CONFIG: Record<GroupHealth, HealthConfig> = {
  healthy: { label: 'Healthy', lightColor: '#217A49', darkColor: '#55C285' },
  managerless: {
    label: 'Managerless',
    lightColor: '#C48A0C',
    darkColor: '#F5CB47',
  },
  ownerless: {
    label: 'Ownerless',
    lightColor: '#C42828',
    darkColor: '#F27070',
  },
  inactive: { label: 'Inactive', lightColor: '#6B7280', darkColor: '#9CA3AF' },
};

export interface HealthChipProps {
  // A single tag, or a comma-separated list of tags (e.g. "ownerless,inactive") when a group
  // matches more than one condition.
  value?: string;
  thresholdDays?: number;
  size?: 'small' | 'medium';
}

function isGroupHealth(tag: string): tag is GroupHealth {
  return tag in HEALTH_CONFIG;
}

export default function HealthChip({
  value,
  thresholdDays = 90,
  size = 'small',
}: HealthChipProps) {
  const theme = useTheme();

  const tags = (value ?? '').split(',').filter(isGroupHealth);
  if (tags.length === 0) {
    return null;
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="nowrap"
      height="100%"
      title={tags
        .map((tag) =>
          tag === 'inactive'
            ? `Inactive ${thresholdDays}d+`
            : HEALTH_CONFIG[tag].label
        )
        .join(', ')}
    >
      {tags.map((tag, index) => {
        const config = HEALTH_CONFIG[tag];
        const label =
          tag === 'inactive' ? `Inactive ${thresholdDays}d+` : config.label;
        const color =
          theme.palette.mode === 'light' ? config.lightColor : config.darkColor;

        return (
          <Stack
            key={tag}
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ flexShrink: 0 }}
          >
            <CircleIcon
              sx={{
                color,
                fontSize: size === 'small' ? 12 : 16,
                display: 'block',
                transform: 'translateY(-1px)',
              }}
            />
            <Typography
              noWrap
              sx={{
                color,
                fontWeight: 500,
                fontSize: size === 'small' ? 14 : 18,
                lineHeight: 1,
              }}
            >
              {label}
            </Typography>
            {index < tags.length - 1 && (
              <Typography
                noWrap
                sx={{
                  color: 'text.secondary',
                  fontSize: size === 'small' ? 14 : 18,
                  lineHeight: 1,
                }}
              >
                ,
              </Typography>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}
